import { useMemo, useState } from "react";
import { EmailLoginEntityReq } from "../types/email-login-otp";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/api";
import { BaseEntityRes } from "../types/base";
import Http from "../services/http";
import { useUserHooks } from "./user-hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  actionSetEmailLoginModalOpen,
  actionSetEmailOtpModalOpen,
  actionSetLoginModalOpen,
} from "../store/actions/login-option-actions";
import { actionSetAuthEmailBanDuration } from "../store/actions/auth-actions";
import {
  COOKIE_GST_ATKN,
  EMAIL_LOGIN_VERIFICATION_RESULT,
  LANGUAGE_TRANS,
  REDIRECT,
} from "../constants/storage";
import { Cookie, LocalStorage } from "../services/storage";
import { getDomain } from "../utils/UtilsBrowser";
import { LoginOptionState } from "../store/reducers/login-option-reducer";
import { ApplicationState } from "../store";
import { UtilsUseHistory } from "../utils/UtilsUseHistory";
import { RCODES } from "../constants/rcodes";
import { UtilsDateTime } from "../utils/UtilsDateTIme";
import Cookies from "js-cookie";
import { LayoutState } from "../store/reducers/layout-reducer";
import { UserState } from "../store/reducers/user-reducer";
import { useAuthProvider } from "./useAuthProvider";
import { useLocation } from "react-router-dom";
import { UtilsUser } from "../utils/UtilsUser";
import { useEmailSupport } from "../utils/email-supported/useEmailSupport";
import { deepLinkService } from "../deeplink/DeepLinkService";

export const useLoginOptionHooks = () => {
  // third party hooks
  const dispatch = useDispatch();
  const location = useLocation();

  // custom hooks
  const { setUserEmailLoginInfoData } = useUserHooks();
  const { login } = useAuthProvider();
  const { isEmailLoginSupported } = useEmailSupport();

  // states
  const [isVerifying, setIsVerifying] = useState(false);
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { isEmailLoginModalOpen, isEmailOtpModalOpen } = useSelector<
    ApplicationState,
    LoginOptionState
  >((state) => state.loginOption);
  const { emailBanDuration: banDuration } = useSelector<
    ApplicationState,
    { emailBanDuration: string }
  >((state) => state.auth);
  const { userCountryCode } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  // variables
  const { go, push } = UtilsUseHistory();

  /**
   * Object with the duration's value and unit of measurement
   * @property {Object} duration
   * @property {string} duration.value  The numerical value of the duration
   * @property {string} duration.did    The DID key for the unit of measurement of the duration
   *                                    e.g.: minutes, hours, or days
   */
  const duration = useMemo(() => {
    const value = banDuration.split(" ")[0];
    let did = "";

    if (banDuration.includes("day")) {
      did = "PRIVACY_LOGIN_EMAIL_OTP_LOCKED_TEXT_DAYS";
    } else if (banDuration.includes("hour") || banDuration.includes("hours")) {
      did = "PRIVACY_LOGIN_EMAIL_OTP_LOCKED_TEXT_HOURS";
    } else if (banDuration.includes("minutes")) {
      did = "PRIVACY_LOGIN_EMAIL_OTP_LOCKED_TEXT_MINS";
    }

    return {
      value,
      did,
    };
  }, [banDuration]);

  /**
   * Execute SA (Samsung Account) login.
   *
   * Reads the auth-safe redirect URL from the saved deep link,
   * then delegates to useAuthProvider.login("SA") which handles:
   * - dispatch(actionLogout) / dispatch(actionResetAuth)
   * - LocalStorage.set(AUTH_TYPE, "SA")
   * - GA: GoogleAnalytics.triggerEvent(EVENT_CATEGORY_HEADER, EVENT_HEADER_ACTION_LOGIN)
   * - window.samsung.account.signIn()
   */
  const executeSALogin = () => {
    const savedDeepLink = deepLinkService.get();
    const redirectURL = savedDeepLink?.authRedirect ?? "";
    login("SA", { redirectURL });
  };

  /**
   * Execute Email (Guest) login.
   *
   * Opens the EmailLoginModal for the user to enter their info.
   * The OTP verification and subsequent redirect are handled by the
   * email-login-hooks and otp-hooks flows.
   * GA for email login is handled by the LoginModal component.
   */
  const executeEmailLogin = () => {
    toggleEmailLoginModal(true);
  };

  /**
   * Centralized login initiation.
   *
   * 1. If already logged in → no-op
   * 2. Resolve destination (param or current pathname)
   * 3. Resolve & save deep link (survives SA portal redirect)
   * 4. Get country code from layout state
   * 5. Decide login method:
   *    - Email NOT supported → executeSALogin()
   *    - Email IS supported → open LoginModal (user picks SA or Email)
   *
   * @param destination The path to redirect to after login. Defaults to current pathname.
   */
  const initiateLogin = (destination?: string) => {
    const isLoggedIn = UtilsUser.isUserLoggedIn();
    if (isLoggedIn) return;

    // Resolve destination: explicit param, or current pathname
    const dest = destination ?? location.pathname;

    // Resolve and save deep link so it survives SA portal redirect.
    // Don't overwrite an existing pending deep link — it may have been saved
    // by DeepLinkChecker with the full ticket destination (e.g., /my-data/SP-DEV-123).
    const resolution = deepLinkService.resolve(dest);
    if (resolution.status === "VALID") {
      const existing = deepLinkService.get();
      if (!existing) {
        deepLinkService.save(resolution.deepLink);
      }
    }

    // Determine the auth-safe redirect URL (whitelist-safe path without ticket)
    const redirect =
      resolution.status === "VALID" ? resolution.deepLink.authRedirect : dest;

    let countryCode: string;

    // get CountryCode
    if (layoutState.selectedCountry.code === "") {
      countryCode = userCountryCode;
    } else {
      countryCode = layoutState.selectedCountry.code;
    }

    // Guard for empty countryCode
    if (!countryCode) {
      console.error("Country not yet set.");
      return;
    }

    // SA Login (email not supported for this country)
    if (!isEmailLoginSupported(countryCode)) {
      executeSALogin();
      return;
    }

    // Email login supported — open LoginModal for user to choose SA or Email
    toggleLoginModal({
      isLoginModalOpen: true,
      loginModalRedirect: redirect,
    });
  };

  const handleEmailSignin = async (
    fname: string,
    lname: string,
    email: string,
    type: "send" | "resend"
  ) => {
    const tz = UtilsDateTime.getLocalTimezone();
    const site_country =
      layoutState.selectedCountry.code ?? layoutState.countryCode;
    let language = Cookies.get(LANGUAGE_TRANS) || layoutState.languageTrans;

    if (layoutState.countryCode === "US" && language === "en") {
      language = "en-rUS";
    }

    const params: EmailLoginEntityReq = {
      fname: fname,
      lname: lname,
      email: email,
      tz,
      site_country,
      language,
    };
    setIsVerifying(true);
    try {
      const url = `${API_BASE_URL}${API_ENDPOINTS.EMAIL_LOGIN_SEND_OTP}`;
      const res: BaseEntityRes = await Http.post(url, params);

      // no need to re-toggle OTP modal
      if (type === "resend") return;

      switch (res.rcode) {
        case RCODES.SUCCESS:
          LocalStorage.set(EMAIL_LOGIN_VERIFICATION_RESULT, "false"); // false value means user is still validating OTP.
          setUserEmailLoginInfoData(params);
          toggleEmailOtpModal(true);
          toggleEmailLoginModal(false);
          setIsVerifying(false);
          break;
        default:
          break;
      }
    } catch (e: any) {
      setIsVerifying(false);

      switch (e.rcode) {
        case RCODES.GUEST_BANNED_15MINS:
        case RCODES.GUEST_BANNED_30MINS:
        case RCODES.GUEST_BANNED_1HR:
        case RCODES.GUEST_BANNED_4HR:
        case RCODES.GUEST_BANNED_1D: {
          //   join(" "): "15 minutes" //   slice(6): ["15", "minutes"] //   split: ["Bad", "Request", "-", "guest", "banned", "for", "15", "minutes"] //   rmsg = "Bad Request - guest banned for 15 minutes" // BREAKDOWN:
          const duration = e.rmsg.split(" ").slice(6).join(" ");
          dispatch(actionSetAuthEmailBanDuration(duration));
          break;
        }
        default:
          console.error(e);
          break;
      }
    }
    setIsVerifying(false);
  };

  const toggleLoginModal = (data: {
    isLoginModalOpen: boolean;
    loginModalRedirect?: string;
  }) => {
    if (data.isLoginModalOpen) {
      // added a new page in the browser history in order to trigger the interceptor for the back button using window.onpopstate
      push({}, "");
    } else if (!isEmailLoginModalOpen || !isEmailOtpModalOpen) {
      // console.log("do nothing");
    } else {
      go(-1);
    }
    dispatch(actionSetLoginModalOpen(data));
  };

  const removeEmailLoginSession = () => {
    if (
      LocalStorage.get(EMAIL_LOGIN_VERIFICATION_RESULT) &&
      LocalStorage.get(EMAIL_LOGIN_VERIFICATION_RESULT) === "false"
    ) {
      LocalStorage.remove(EMAIL_LOGIN_VERIFICATION_RESULT);
    }
  };

  const removeGstToken = () => {
    const hostname = globalThis.location.hostname;
    const domain = getDomain(hostname);
    Cookie.remove(COOKIE_GST_ATKN, {
      path: "/",
      domain: domain,
    });
  };

  const removeRedirectSession = () => {
    sessionStorage.removeItem(REDIRECT);
  };

  const handleSignInClick = () => {
    Cookie.remove("REDIRECT_URI");
    initiateLogin();
  };

  const toggleEmailLoginModal = (data: boolean) => {
    dispatch(actionSetEmailLoginModalOpen(data));
    dispatch(actionSetAuthEmailBanDuration(""));
  };

  const toggleEmailOtpModal = (data: boolean) => {
    dispatch(actionSetEmailOtpModalOpen(data));
    dispatch(actionSetAuthEmailBanDuration(""));
  };

  return {
    handleEmailSignin,
    toggleLoginModal,
    toggleEmailOtpModal,
    toggleEmailLoginModal,
    removeEmailLoginSession,
    removeGstToken,
    removeRedirectSession,
    isVerifying,
    duration,
    initiateLogin,
    executeSALogin,
    executeEmailLogin,
    handleSignInClick,
  };
};
